import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  jobsApi,
  screeningQuestionsApi,
  type JobPayload,
  type JobStatus,
  type ScreeningQuestionPayload,
} from '@/features/recruiter/api/jobs.api'

export const RECRUITER_JOBS_KEY = ['recruiter', 'jobs'] as const
export const recruiterJobKey = (id: number) => ['recruiter', 'jobs', id] as const

export function useRecruiterJobsQuery(params: { status?: string; page?: number; per_page?: number } = {}) {
  return useQuery({
    queryKey: [...RECRUITER_JOBS_KEY, params],
    queryFn: () => jobsApi.list(params),
    placeholderData: (previous) => previous,
  })
}

export function useRecruiterJobQuery(jobId: number | undefined) {
  return useQuery({
    queryKey: recruiterJobKey(jobId ?? 0),
    queryFn: () => jobsApi.get(jobId as number),
    enabled: jobId != null,
  })
}

export function useJobSourceOptions() {
  return useQuery({
    queryKey: ['recruiter', 'job-source-options'],
    queryFn: jobsApi.sourceOptions,
    staleTime: 10 * 60 * 1000,
  })
}

export function useLocationStates() {
  return useQuery({
    queryKey: ['recruiter', 'locations', 'states'],
    queryFn: jobsApi.states,
    staleTime: 60 * 60 * 1000,
  })
}

export function useLocationCities(stateId: number | undefined) {
  return useQuery({
    queryKey: ['recruiter', 'locations', 'cities', stateId ?? 0],
    queryFn: () => jobsApi.cities(stateId as number),
    enabled: stateId != null,
    staleTime: 60 * 60 * 1000,
  })
}

export function useCreateJob() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: JobPayload) => jobsApi.create(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: RECRUITER_JOBS_KEY }),
  })
}

export function useUpdateJob(jobId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: JobPayload) => jobsApi.update(jobId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: RECRUITER_JOBS_KEY })
      void queryClient.invalidateQueries({ queryKey: recruiterJobKey(jobId) })
    },
  })
}

export function useChangeJobStatus(jobId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (status: JobStatus) => jobsApi.changeStatus(jobId, status),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: RECRUITER_JOBS_KEY })
      void queryClient.invalidateQueries({ queryKey: recruiterJobKey(jobId) })
    },
  })
}

export function useUploadJd(jobId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (file: File) => jobsApi.uploadJd(jobId, file),
    onSuccess: (updated) => {
      queryClient.setQueryData(recruiterJobKey(jobId), updated)
      void queryClient.invalidateQueries({ queryKey: RECRUITER_JOBS_KEY })
    },
  })
}

export function useDeleteJd(jobId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => jobsApi.deleteJd(jobId),
    onSuccess: (updated) => {
      queryClient.setQueryData(recruiterJobKey(jobId), updated)
      void queryClient.invalidateQueries({ queryKey: RECRUITER_JOBS_KEY })
    },
  })
}

export function useScreeningQuestionMutations(jobId: number) {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: recruiterJobKey(jobId) })

  const create = useMutation({
    mutationFn: (payload: ScreeningQuestionPayload) => screeningQuestionsApi.create(jobId, payload),
    onSuccess: invalidate,
  })
  const update = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: ScreeningQuestionPayload }) =>
      screeningQuestionsApi.update(jobId, id, payload),
    onSuccess: invalidate,
  })
  const remove = useMutation({
    mutationFn: (id: number) => screeningQuestionsApi.delete(jobId, id),
    onSuccess: invalidate,
  })

  return { create, update, remove }
}
